import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { Switch, NavLink as Link, useLocation } from 'react-router-dom'
import Spinner from 'ui-components/lib/components/Spinner'
import BaseLayout from 'ui-components/lib/components/BaseLayout'
import { useFormik } from 'formik'
import { useLazyQuery, useQuery } from '@apollo/react-hooks'
import { demandsQueries, walletQueries, clientsQueries } from '../graphql/queries'
import { Wallet } from '../models'
import * as routes from './routes'
import DashboardContext from '../contexts/DashboardContext'
import PrivateRoute from '../components/PrivateRouter'
import NavHeader from '../components/NavHeader'
import Cart from '../components/Cart'
import RatingModal from '../components/Modal/RatingModal'
import useWindowDimensions from '../hooks/useWindowDimensions'
import logoInline from '../assets/img/logos/logo.svg'
import shortid from 'shortid'
import SmallLogo from "../images/SmallLogo.png"
import { IoIosArrowDropleft, IoIosArrowDropright } from 'react-icons/io'
import {IoCaretDown } from "react-icons/io5";
import * as authService from '../services/auth'
import * as localStorageService from '../services/localStorageService'
import useBeforeunload from '../services/useBeforeUnload'
import './DashboardRoutes.css'
import '../assets/global.css';
import 'bootstrap/dist/css/bootstrap.min.css'; 
import PrivacyLogo from "../assets/img/logos/dataPrivacy.svg"
import SubtabsDropdown from '../components/SubtabDropdown'
import { REVISIA_AUTH_REDIRECT_URL } from '../utils/revisia'
import {
  LayoutDashboard,
  FileText,
  FolderKanban,
  Building2,
  Users,
  Sparkles,
  PenTool,
  BadgeCheck,
} from 'lucide-react'
import CreateDemandModal from "../components/CreateDemandModal/CreateDemandModal";
import { ToastContainer } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import mergeBonuzPlansWallet from '../utils/mergeBonuzPlansWallet'

const DashboardSidebar = React.memo(({openSidebar, handleCloseMenu, accountPermissions, ...rest}) => {
  const location = useLocation();
  const { width } = useWindowDimensions()
  const isMobile = useMemo(() => width <= 600, [width])
  const [openModal, setOpenModal] = useState(false)
  const handeCloseModal = useCallback(() => {
    setOpenModal(prev => !prev)
  }, [])

  let menuToFilter = [
    {
      text: 'Início',
      path: '/dashboard',
      icon: <LayoutDashboard className='svg' style={{width:'20px',height:'20px', strokeWidth: 2}} />,
      routeTo: 'dashboard',
    },
    {
      text: 'Meus serviços',
      path: '/dashboard/serviços',
      icon: <FileText className='svg' style={{width:'20px',height:'20px', strokeWidth: 2}}/>,
      routeTo: 'services-list',
    },
    {
      text: 'Gestão de Contratos',
      path: '/dashboard/bonuz',
      icon: <FolderKanban className='svg' style={{width:'20px',height:'20px', strokeWidth: 2}}/>,
      routeTo: 'contractual-management',
      badge: 'Novo',
      highlight: true,
      externalUrl: REVISIA_AUTH_REDIRECT_URL,
      isExternal: true,
    },
    {
      text: 'Assinatura eletrônica',
      path: '/dashboard/bonuz',
      icon: <PenTool className='svg' style={{width:'20px',height:'20px', strokeWidth: 2}}/>,
      routeTo: 'bonuz-sign',
      badge: 'Novo',
      highlight: true,
      externalUrl: REVISIA_AUTH_REDIRECT_URL,
      isExternal: true,
    },
    {
      text: 'Registros INPI',
      path: '/dashboard/registros-inpi',
      icon: <BadgeCheck className='svg' style={{width:'20px',height:'20px', strokeWidth: 2}}/>,
      routeTo: 'registros-inpi',
      badge: 'Novo',
    },
    {
      text: 'Revisia',
      path: '/dashboard/bonuz',
      icon: <Sparkles className='svg' style={{width:'20px',height:'20px', strokeWidth: 2}} />,
      routeTo: 'bonuz',
      externalUrl: REVISIA_AUTH_REDIRECT_URL,
      isExternal: true,
      badge: 'Novo',
    },
    {
      text: 'Para empresas',
      icon: <Building2 className='svg' style={{width:'20px',height:'20px', strokeWidth: 2}}/>,
      path: '/dashboard/para-empresas',
      routeTo: 'for-copmpanies',
    },
    {
      text: 'Parceiros',
      icon: <Users className='svg' style={{width:'20px',height:'20px', strokeWidth: 2}}/>,
      path: '/dashboard/parceiros',
      routeTo: 'partners',          
    },
  ]

  let menuItems = menuToFilter

  if(accountPermissions){
    menuItems = menuToFilter.map((item, index) => {
      let subItemMap

      if(item.subtabs){
        subItemMap = item.subtabs.filter(item => accountPermissions.permissions.includes(item.routeTo))
      }

      return {
        ...item,
        ...subItemMap && {subtabs: subItemMap}
      }
    })
  }

  const valdateRouteActive = (location, path) => {
    if (path === '/dashboard') {
      return location === path;
    }
    return location.startsWith(path);
  }

  const handleLinkClick = useCallback(() => {
    // Fechar menu em mobile quando um link é clicado
    if (isMobile) {
      handleCloseMenu(false)
    }
  }, [isMobile, handleCloseMenu])

  return (
    <>
      <div className='sidebar'>
        {!openSidebar ?
          <div className="nav-left">
            <div className="nav-logo">
              <button ><img src={SmallLogo} alt="bonuz logo, an owl" className="logo" /></button>
            </div>
            <div className="link">
                {menuItems?.map((item) => {
                    if (item.isExternal && item.externalUrl) {
                        return (
                          <a 
                            href={item.externalUrl} 
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ textDecoration: 'none' }}
                            onClick={handleLinkClick}
                          >
                            <div className="nav-icon">
                                <div className="icon-container">{item.icon}</div>
                            </div>
                          </a>
                        )
                    } else if (item.emBreve !== true && !item.subtabs) {
                        return (
                          <Link 
                            to={item.path} 
                            activeClassName="link-active"
                            exact={true}
                            onClick={handleLinkClick}
                          >
                            <div className="nav-icon">
                                <div className="icon-container">{item.icon}</div>
                            </div>
                          </Link>
                        )
                    } else if (item.subtabs) {
                      return (
                        <div className="nav-icon" onClick={() => handleCloseMenu(!openSidebar)}>
                          <div className="icon-container">
                              {item.icon}
                          </div>
                        </div>
                      )
                    }
                    else {
                      return (
                        <div className="nav-icon">
                          <div className="icon-container">
                              {item.icon}
                          </div>
                        </div>
                      )
                    }
                })}
            </div>

            <div className="expandir" onClick={() => handleCloseMenu(!openSidebar)}>
                <IoIosArrowDropright />
                <p>Expandir</p>
            </div>
          </div>
        : 
          <div className="nav-left open">
            <div className="nav-logo">
                <button ><img src={location.pathname === "/dashboard/para-empresas/data-privacy" ? PrivacyLogo : logoInline} alt="bonuz logo, an owl" className="logo"/></button>
            </div>
            <div className="link">
              {menuItems?.map((item) => {
                    if (item.isExternal && item.externalUrl) {
                        return (
                            <a 
                              href={item.externalUrl} 
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ textDecoration: 'none' }}
                              onClick={handleLinkClick}
                            >
                              <div className={`nav-icon ${item.highlight ? 'highlight' : ''}`}>
                                  <div className="icon-container">
                                      <div>{item.icon}</div>
                                      <div><p>{item.text}</p></div>
                                      {item.badge && <span className="nav-badge">{item.badge}</span>}
                                  </div>
                              </div>
                            </a>
                        )
                    } else if (item.emBreve === true) {
                        return (
                            <div className={`nav-icon ${item.highlight ? 'highlight' : ''}`} style={{cursor: "pointer"}}>
                            <div className="icon-container">
                                <div>{item.icon}</div>
                                <div><p>{item.text}</p></div>
                                {item.badge && <span className="nav-badge">{item.badge}</span>}
                            </div>
                        </div>
                        )
                    }else if(item.subtabs) {
                      return (
                          <SubtabsDropdown className="nav-icon" >
                            <div className="icon-container">
                                <div>{item.icon}</div>
                                <div><p>{item.text}</p></div>
                                <div><IoCaretDown style={{marginLeft: "10px"}}/></div>
                            </div>
                            <div className='subtabs'>
                              {item.subtabs.map(item => {
                                if( item.openModal ) {
                                  return (
                                    <Link
                                      to={item.path}
                                      activeClassName="link-active"
                                      onClick={(e)=> {
                                        item.openModal(e)
                                        handleLinkClick()
                                      }}
                                      exact={true}
                                      isActive={(match, location) => valdateRouteActive(location.pathname, item.path)}
                                    >
                                      <div className="nav-icon">
                                        <div className="icon-container">

                                          <div><p>{item.text}</p></div>
                                          <div>{item.icon}</div>
                                        </div>
                                      </div>
                                    </Link>
                                  )
                                } else{
                                  return (
                                    <Link
                                      to={item.path}
                                      activeClassName="link-active"
                                      onClick={handleLinkClick}
                                      exact={true}
                                      isActive={(match, location) => valdateRouteActive(location.pathname, item.path)}
                                    >
                                      <div className="nav-icon">
                                        <div className="icon-container">

                                          <div><p>{item.text}</p></div>
                                          <div>{item.icon}</div>
                                        </div>
                                      </div>
                                    </Link>
                                  )
                                }}
                              )}
                            </div>
                          </SubtabsDropdown>
                      )
                    } else {
                        return(
                            <Link
                              to={item.path} 
                              activeClassName="link-active"
                              exact={true}
                              isActive={(match, location) => valdateRouteActive(location.pathname, item.path)}
                              onClick={handleLinkClick}
                            >
                              <div className={`nav-icon ${item.highlight ? 'highlight' : ''}`}>
                                  <div className="icon-container">
                                      <div>{item.icon}</div>
                                      <div><p>{item.text}</p></div>
                                      {item.badge && <span className="nav-badge">{item.badge}</span>}
                                  </div>
                              </div>
                            </Link>
                        )
                    }
                })}
            </div>

          <div className="expandir recuar" onClick={() => handleCloseMenu(!openSidebar)}>
              <div><IoIosArrowDropleft /></div>
              <div><p>Recuar</p></div>
          </div>
        </div>
        }
        <CreateDemandModal
          openModal={openModal}
          handleClose={handeCloseModal}
        />
        <ToastContainer />
      </div>
    </>
  )
})

DashboardSidebar.displayName = 'DashboardSidebar'

const DashboardRoutes = () => {
  const { width } = useWindowDimensions()
  const isMobile = useMemo(() => width <= 600, [width])
  const [menuIsOpen, setMenuIsOpen] = useState(() => {
    // Inicializar baseado no tamanho da tela apenas uma vez
    if (typeof window !== 'undefined') {
      return window.innerWidth > 600
    }
    return true
  })
  const [packageId, setPackageId] = useState('false')

  const [openRatingModal, setOpenRatingModal] = useState(false)
  const [demandRatingData, setDemandRatingData] = useState('')

  const [clientAccountPermissions, setClientAccountPermissions] = useState(false)

  const [clientWallet, setClientWallet] = useState('')
  const [clientSubscription, setClientSubscription] = useState(false)
  const [clientSubscriptionIdentifier,setClientSubscriptionIdentifier] = useState('')
  const [fetchMyWallet, { data: dataMyWallet }] = useLazyQuery(walletQueries.GET_MY_WALLET)

  const { data: dataMyName, loading: loadingMyName } = useQuery(clientsQueries.GET_MY_NAME)

  const { data: dataAccountPermissions } = useQuery(clientsQueries.GET_ACCOUNT_PERMISSIONS, {
    onCompleted(data){
      setClientAccountPermissions(dataAccountPermissions.getAccountPermissions)
    }
  })

  const { data: subscriptionBonuzPlans, loading:subscriptionLoading} = useQuery(walletQueries.GET_MY_SUBSCRIPTION, {
    variables: {
      subscriptionType: 'planos-bonuz',
      planIdentifier: 'bonuz-plans',
    }
  })

  const { data: subscriptionDataPrivacy, loading:subscriptionLoadingDataPrivacy} = useQuery(walletQueries.GET_MY_SUBSCRIPTION, {
    variables: {
      subscriptionType: 'data-privacy',
    }
  })

  useEffect(() => {
    const sub = subscriptionBonuzPlans && subscriptionBonuzPlans.subscription
    if (sub && sub.active && sub.planIdentifier) {
      setClientSubscription(true)
      setClientSubscriptionIdentifier(sub.planIdentifier)
    } else {
      setClientSubscription(false)
      setClientSubscriptionIdentifier('')
    }
  }, [subscriptionBonuzPlans])

  const { data: openEvaluationData, loading: openEvaluationLoading } = useQuery(demandsQueries.GET_OPEN_EVALUATION)

  useEffect(() => {
    const openEvaluation = localStorageService.getStorage('demand-evaluation')

    if(!openEvaluationLoading && !openEvaluation && openEvaluationData){
      setDemandRatingData({demandId: openEvaluationData?.getOpenEvaluation?.demandId, lawyerId: openEvaluationData?.getOpenEvaluation?.lawyerId})
      setOpenRatingModal(true)
    }
  }, [openEvaluationData, openEvaluationLoading])

  const largeWidth = width >= 1640 && true

  const menuItems = useMemo(() => {
    const userMenu = [
      {
        text: 'Meu Perfil',
        path: '/dashboard/painel',
        routeTo: 'client-panel',
      },
      {
        text: 'Minha Conta',
        path: '/dashboard/minha-conta',
        routeTo: 'my-account',
      },
      {
        text: 'Minha Carteira',
        path: '/dashboard/carteira',
        routeTo: 'wallet',
      },
      {
        text: 'Minha Equipe',
        path: '/dashboard/minha-equipe',
        routeTo: 'my-team',
      },
      {
        text: 'Sair',
        path: '/sair',
        routeTo: 'logout',
      },
    ]
    
    if(clientAccountPermissions){
      return userMenu.filter((element) => {
        return clientAccountPermissions.permissions.find(item => item === element.routeTo)
      })
    }
    return []
  }, [clientAccountPermissions])

  const formik = useFormik({
    initialValues: new Wallet(),
  })

  useEffect(() => {
    fetchMyWallet()
  }, [fetchMyWallet])


  useEffect(() => {
    const rawWallet = dataMyWallet?.wallet
    if (!rawWallet) return
    const mergedWallet = mergeBonuzPlansWallet({ ...rawWallet }, subscriptionBonuzPlans?.subscription)
    formik.setValues(mergedWallet)
    /* Mesmo objeto do CreditsBalloon / Cart — cópia mesclada, sem mutar cache Apollo */
    setClientWallet(dataMyWallet ? { ...dataMyWallet, wallet: mergedWallet } : { wallet: mergedWallet })
  }, [dataMyWallet, subscriptionBonuzPlans]) // eslint-disable-line

  useEffect(() => {
    if(largeWidth){
      setMenuIsOpen(true)
    }
    // Não forçar fechamento em mobile aqui para permitir que o usuário abra manualmente
  }, [largeWidth])

  const toggleMenu = useCallback(() => {
    setMenuIsOpen(prev => !prev)
  }, [])

  const closeMenu = useCallback(() => {
    setMenuIsOpen(false)
  }, [])

  // Controlar scroll do body quando menu está aberto em mobile - usando apenas CSS
  useEffect(() => {
    if (isMobile && menuIsOpen) {
      document.documentElement.classList.add('menu-open')
      return () => {
        document.documentElement.classList.remove('menu-open')
      }
    } else if (isMobile && !menuIsOpen) {
      document.documentElement.classList.remove('menu-open')
    }
  }, [menuIsOpen, isMobile])

  const decodedToken = authService.decodedToken()

  useBeforeunload((event) => {
    if(decodedToken.keepAlive && decodedToken.keepAlive === 'false'){
      authService.onLogout()
      return true
    } else {
      return true
    }
  });

  const sidebarComponent = useMemo(() => (
    <DashboardSidebar 
      openSidebar={menuIsOpen} 
      onClose={closeMenu} 
      handleCloseMenu={toggleMenu} 
      accountPermissions={clientAccountPermissions} 
    />
  ), [menuIsOpen, closeMenu, toggleMenu, clientAccountPermissions])

  const navHeaderComponent = useMemo(() => (
    <NavHeader 
      clientName={ 
        loadingMyName ? <Spinner color="primary" /> : dataMyName?.me?.companyName || `${dataMyName?.me?.firstName} ${dataMyName?.me?.lastName}` 
      }
      bnzBalance={(
        subscriptionBonuzPlans?.subscription?.planIdentifier === 'plano-essencial' ||
        subscriptionBonuzPlans?.subscription?.planIdentifier === 'plano-start'
          ? formik.values.creditsBonuzPlans
          : formik.values.balance
      ) || '0'}
      isEssencial={subscriptionBonuzPlans?.subscription?.planIdentifier === 'plano-essencial'}
      plansLoading={subscriptionLoading}
      profileMenuItems={menuItems}
      dataBonuzPlan={subscriptionBonuzPlans}
      dataPrivacy={subscriptionDataPrivacy}
      dataPrivacyLoading={subscriptionLoadingDataPrivacy}
      menuIsOpen={menuIsOpen}
      onToggleMenu={toggleMenu}
    />
  ), [
    loadingMyName, 
    dataMyName, 
    subscriptionBonuzPlans, 
    formik.values.creditsBonuzPlans, 
    formik.values.balance,
    subscriptionLoading,
    menuItems,
    subscriptionDataPrivacy,
    subscriptionLoadingDataPrivacy,
    menuIsOpen,
    toggleMenu
  ])
  
  // Memoizar o conteúdo das rotas - só re-renderizar se clientAccountPermissions mudar
  // Usar key estável para evitar re-montagem quando menuIsOpen muda
  const routesContent = useMemo(() => (
    <Switch key="dashboard-routes">
      {routes.dashboard.map(({ ...props }) => { //eslint-disable-line
        let newProps = { ...props }
        if(clientAccountPermissions && !clientAccountPermissions.permissions.find(item => item === props.routeTo)){
          newProps.isAuthorized = false
        }
        return <PrivateRoute key={props.path || shortid.generate()} {...newProps} />
      }
      )}
    </Switch>
  ), [clientAccountPermissions])

  // Memoizar o BaseLayout props para evitar re-renders desnecessários
  const baseLayoutProps = useMemo(() => ({
    id: "base-layout",
    title: '',
    cornerContent: navHeaderComponent,
    menuIsOpen: menuIsOpen,
    handleChangeMenuIsOpen: toggleMenu,
    menuWidth: 0,
    style: {background: '#FFFFFF'},
    sidebar: sidebarComponent
  }), [navHeaderComponent, menuIsOpen, toggleMenu, sidebarComponent])

  // Overlay renderizado fora do BaseLayout para evitar re-renders
  const overlay = useMemo(() => {
    if (isMobile && menuIsOpen) {
      return (
        <div 
          className={`menu-overlay active`} 
          onClick={closeMenu}
        />
      )
    }
    return null
  }, [isMobile, menuIsOpen, closeMenu])

  return (
    <DashboardContext.Provider value={[ packageId, setPackageId ]}>
      {overlay}
      <BaseLayout {...baseLayoutProps}>
        {routesContent}
      </BaseLayout>
      <Cart menuIsOpen={menuIsOpen} wallet={clientWallet} subscription={clientSubscription} subscriptionIdentifier={clientSubscriptionIdentifier} />
      <RatingModal 
        openModal={openRatingModal}
        handleClose={() => setOpenRatingModal(false)}
        demandId={demandRatingData.demandId}
        lawyerId={demandRatingData.lawyerId}
      />
    </DashboardContext.Provider>
  )
}

export default DashboardRoutes
